/**
 * 상한까지만 기다린다 — **그리고 늦은 값을 버리지 않는다.**
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 *
 * 브리지 너머의 셸은 **답하지 않을 수 있다.** `bridge.ts` 의 `askShell` 은 던지는 것만
 * 막는데(그 주석이 정확히 그것만 말한다) 안 풀리는 `Promise` 는 그 그물을 지난다.
 * 부팅이 그런 대기에 걸리면 **기기 등록·heartbeat·전환 이벤트가 함께 죽고** 화면은
 * 멀쩡하다 — 시청 화면이 초기화를 기다리지 않도록 만들어 뒀기 때문이다(`App.tsx`).
 * 2026-08-12 데모에서 iPhone 이 정확히 그 상태였다.
 *
 * ── `checkout.ts` 의 `waitForFlush` 와 무엇이 다른가 ────────
 *
 * 그쪽은 「기록보다 이탈이 우선이다」라 **기다렸다는 사실만** 있으면 된다. 이쪽은 둘이
 * 더 필요하다.
 *
 * | | 왜 |
 * |---|---|
 * | **값을 돌려준다** | 제때 왔으면 그것으로 등록해야 한다 |
 * | **넘겼는지 알려 준다** | 넘겼을 때만 늦은 값으로 다시 등록한다. `value === fallback` 으로는 「제때 온 null」과 구분되지 않는다 |
 *
 * ── 왜 `schedule` 을 받는가 ─────────────────────────────────
 *
 * 이 패키지는 **jsdom 없이 node 환경**에서 테스트한다(`routing.ts`·`client-type.ts` 와
 * 같은 자리). 시계를 주입하면 검사가 **실제로 기다리지 않고** 상한 경로를 밟는다 —
 * `setTimeout` 을 직접 부르면 그 경로는 「2초짜리 테스트」가 되거나 아예 검사되지 않는다.
 */
export interface Settled<T> {
  value: T;
  /** 상한을 넘겼다. **늦은 값을 쓰려면 원래 `Promise` 를 호출부가 붙들고 있어야 한다.** */
  timedOut: boolean;
}

/** `setTimeout` 과 같은 모양. 테스트가 즉시 부르는 가짜를 넘긴다. */
export type Schedule = (fn: () => void, ms: number) => void;

/**
 * `pending` 이 `ms` 안에 풀리면 그 값을, 아니면 `fallback` 을 준다.
 *
 * **던지지 않는다.** reject 도 `fallback` 으로 받는다 — 등록 하나가 부팅을 무너뜨리면
 * 안 되고, 그 방침은 `registerWithBloom` 의 `try/catch` 와 같다.
 *
 * **`pending` 을 취소하지 않는다.** 호출부가 그것을 계속 들고 있다가 늦게 풀리면 쓴다.
 */
export function settleWithin<T>(
  pending: Promise<T>,
  ms: number,
  fallback: T,
  schedule: Schedule,
): Promise<Settled<T>> {
  return Promise.race([
    pending.then(
      (value) => ({ value, timedOut: false }),
      () => ({ value: fallback, timedOut: false }),
    ),
    new Promise<Settled<T>>((resolve) => {
      schedule(() => {
        resolve({ value: fallback, timedOut: true });
      }, ms);
    }),
  ]);
}
